/**
 * Notification Center — bell icon with badge and dropdown panel.
 * Placed in Header to the left of the user menu.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { fetchNotifications } from '@/services/notificationApi';
import { NotificationTypeIcon, getAccentClass } from '@/components/notifications/NotificationTypeIcon';
import { NotificationPreferences } from '@/components/notifications/NotificationPreferences';
import type { NotificationRecord } from '@/types/notifications';

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationCenter() {
  const { unreadCount, markRead, markAllRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Fetch notifications when dropdown opens
  const openDropdown = useCallback(async () => {
    setIsOpen(true);
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchNotifications();
      setItems(data.slice(0, 20));
    } catch {
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openDropdown();
    }
  };

  const handleItemClick = async (item: NotificationRecord) => {
    if (!item.is_read) {
      await markRead(item.notification_id);
      setItems((prev) =>
        prev.map((n) => (n.notification_id === item.notification_id ? { ...n, is_read: true } : n))
      );
    }
    setIsOpen(false);
    if (item.metadata?.link_target) {
      navigate(item.metadata.link_target);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleRetry = () => {
    openDropdown();
  };

  const badgeText = unreadCount === null ? null : unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="relative flex items-center justify-center rounded-lg p-2 text-dark-300 transition-all hover:bg-dark-800 hover:text-dark-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {/* Badge */}
        {badgeText && unreadCount! > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {badgeText}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl border border-dark-700 bg-dark-800/95 backdrop-blur-xl shadow-xl animate-fade-in overflow-hidden z-50"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-dark-700 px-4 py-3">
            <h3 className="text-sm font-medium text-dark-100">Notifications</h3>
            {items.some((n) => !n.is_read) && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                role="menuitem"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-3 w-3/4 rounded bg-dark-700 mb-2" />
                    <div className="h-2 w-1/2 rounded bg-dark-700" />
                  </div>
                ))}
              </div>
            )}

            {!isLoading && error && (
              <div className="p-4 text-center">
                <p className="text-sm text-rose-400 mb-2">{error}</p>
                <button
                  onClick={handleRetry}
                  className="text-xs text-dark-300 hover:text-dark-100 underline"
                >
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !error && items.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-sm text-dark-400">No notifications</p>
              </div>
            )}

            {!isLoading && !error && items.length > 0 && (
              <div className="divide-y divide-dark-700/30">
                {items.map((item) => (
                  <button
                    key={item.notification_id}
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left px-4 py-3 hover:bg-dark-700/50 transition-colors border-l-2 ${
                      item.is_read
                        ? 'border-l-transparent'
                        : getAccentClass(item.notification_type)
                    }`}
                    role="menuitem"
                  >
                    <div className="flex items-start gap-3">
                      <NotificationTypeIcon
                        type={item.notification_type}
                        symbol={item.metadata?.symbol}
                        expirationDate={item.metadata?.expiration_date}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm truncate ${
                            item.is_read ? 'text-dark-300 font-normal' : 'text-dark-100 font-medium'
                          }`}
                        >
                          {item.title}
                        </p>
                        <p className="text-xs text-dark-400 mt-0.5">
                          {formatRelativeTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preferences */}
          <NotificationPreferences />
        </div>
      )}
    </div>
  );
}
